import React, { useRef, useState } from 'react';
import { BaseButton, Input, useAuth, useT } from '@repo/common';
import {
  socialValidateEmail,
  updateEmail,
  validateEmail,
} from '../../../../api/user/user.api';

const getInputState = (value, focused, validationState) => {
  if (validationState === 'social-failed') validationState = 'failed';
  if (['failed', 'success', 'warning'].includes(validationState))
    return validationState;
  if (focused) return 'active';
  return 'default';
};

const getEmailMessage = (status, showError, t) => {
  if (!showError) return '';
  switch (status) {
    case 'social-failed':
      return t(
        'This email is already registered. Sign in with Google.',
        '이미 등록된 이메일입니다. Google로 로그인해 주세요.',
      );
    case 'failed':
      return t('This email is already registered', '이미 등록된 이메일입니다');
    case 'warning':
      return t('Check your email format', '이메일 형식을 확인해 주세요');
    case 'success':
      return t('All set!', '사용 가능합니다!');
    default:
      return '';
  }
};
const NewEmailForm = ({ onClose, onSuccess }) => {
  const t = useT();
  const { user, refreshUser } = useAuth();
  const emailInputRef = useRef(null);
  const [newEmail, setNewEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('default');
  const [emailFocused, setEmailFocused] = useState(false);
  const emailDebounceTimer = useRef(null);
  const [showEmailError, setEmailShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const effectiveEmailStatus = getInputState(
    newEmail,
    emailFocused,
    emailStatus,
  );

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setNewEmail(value);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!value) {
      setEmailStatus('default');
      setEmailShowError(false);
      return;
    }

    if (!emailRegex.test(value)) {
      setEmailStatus('warning');
      setEmailShowError(true);
      return;
    }

    if (emailDebounceTimer.current) clearTimeout(emailDebounceTimer.current);
    emailDebounceTimer.current = setTimeout(async () => {
      try {
        const [validateRes, socialRes] = await Promise.allSettled([
          validateEmail(value),
          socialValidateEmail(value),
        ]);

        if (
          socialRes.status === 'rejected' &&
          socialRes.reason?.response?.status === 409
        ) {
          setEmailStatus('social-failed');
          setEmailShowError(true);
        } else if (
          validateRes.status === 'rejected' &&
          validateRes.reason?.response?.status === 409
        ) {
          setEmailStatus('failed');
          setEmailShowError(true);
        } else {
          setEmailStatus('success');
          setEmailShowError(true);
        }
      } catch (err) {
        console.error('이메일 검증 에러', err);
        setEmailStatus('failed');
        setEmailShowError(true);
      }
    }, 700);
  };

  const handleSubmit = async () => {
    if (emailStatus !== 'success') return;
    setIsLoading(true);
    try {
      await updateEmail(newEmail);
      await refreshUser();
      onClose?.();
      onSuccess?.();
    } catch (err) {
      console.error('이메일 변경 실패', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='flex flex-col items-start tabletH:items-end pt-4 pb-10 tabletH:pt-0 tabletH:pb-6 w-full gap-[30px] tabletH:gap-4'>
      <Input
        id='email-input'
        ref={emailInputRef}
        label=''
        placeholder={t('Enter a new email address', '새 이메일 주소를 입력해 주세요')}
        value={newEmail}
        state={effectiveEmailStatus}
        message={getEmailMessage(emailStatus, showEmailError, t)}
        onChange={handleEmailChange}
        hideCount
        showStatusIcon={
          emailStatus === 'success' || emailStatus === 'social-failed'
        }
        onFocus={() => setEmailFocused(true)}
        onBlur={() => setEmailFocused(false)}
        className='w-full'
      />

      <BaseButton
        hideIcon
        width={user?.email ? '80px' : '120px'}
        isLoading={isLoading}
        isDisabled={emailStatus !== 'success'}
        onClick={handleSubmit}
      >
        {user?.email ? t('Change', '변경') : t('Add email', '이메일 추가')}
      </BaseButton>
    </div>
  );
};

export default NewEmailForm;
